const RESET_CODE_LENGTH = 6;

export function normalizeResetCode(value) {
  return (value ?? '').replace(/\D/g, '').slice(0, RESET_CODE_LENGTH);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const trimmed = (value ?? '').toString().trim();
    if (trimmed) return trimmed;
  }
  return '';
}

/** `useLocation().state` es `null` al abrir un link de mail (no un navigate con state). */
function readLocationState(locationState) {
  return locationState ?? {};
}

/**
 * Query string de un link HashRouter.
 * `window.location.search` queda vacío: los params van dentro del hash.
 * Ejemplo: `#/reset-password?email=...&code=...`
 *
 * @param {string} [hash]
 * @returns {URLSearchParams}
 */
export function getHashSearchParams(hash = typeof window !== 'undefined' ? window.location.hash : '') {
  const query = String(hash).split('?')[1] ?? '';
  return new URLSearchParams(query);
}

/**
 * @param {URLSearchParams | null | undefined} searchParams
 * @param {string} key
 * @param {string} [hash]
 * @returns {string}
 */
export function getAuthQueryValue(searchParams, key, hash) {
  const hashParams = getHashSearchParams(hash);
  return firstNonEmpty(searchParams?.get?.(key), hashParams.get(key));
}

/**
 * Lee email y código del reset desde query del HashRouter (hash o search del router).
 * URL esperada: /#/reset-password?email=...&code=...
 *
 * @param {URLSearchParams | null | undefined} searchParams
 * @param {{ email?: string, code?: string }} [locationState]
 * @param {string} [hash]
 */
export function getResetPasswordParams(searchParams, locationState = {}, hash) {
  const state = readLocationState(locationState);
  const email = firstNonEmpty(
    getAuthQueryValue(searchParams, 'email', hash),
    state.email,
  );
  const code = normalizeResetCode(
    firstNonEmpty(
      getAuthQueryValue(searchParams, 'code', hash),
      state.code,
    ),
  );

  return {
    email,
    code,
    isValid: Boolean(email) && code.length === RESET_CODE_LENGTH,
  };
}

/**
 * Lee email y código de verificación desde el hash del mail Cognito.
 * URL esperada: /#/verify?email=...&code=...
 *
 * @param {URLSearchParams | null | undefined} searchParams
 * @param {{ email?: string, code?: string }} [locationState]
 * @param {string} [hash]
 */
export function getVerifyEmailParams(searchParams, locationState = {}, hash) {
  const state = readLocationState(locationState);
  const email = firstNonEmpty(
    getAuthQueryValue(searchParams, 'email', hash),
    state.email,
  );
  const code = normalizeResetCode(
    firstNonEmpty(
      getAuthQueryValue(searchParams, 'code', hash),
      state.code,
    ),
  );

  return { email, code };
}
